import {
    useQuery,
    useMutation
} from "@tanstack/react-query";
import { Council } from "@/types";
import { AxiosError } from "axios";
import axios, { baseUrl } from "@/axios.config";
import { ApiResponse } from "@/types/fetch.type";

export const useGetAllCouncils = () => {
    return useQuery<ApiResponse<Council[]>, AxiosError<{ message: string }>>({
        queryKey: ["councils"],
        queryFn: async () => {
            const response = await axios.get("/tblcouncil");
            return response.data;
        },
    });
}

interface CreateCouncilDTO {
    councilName: string;
    councilCode: string;
    councilProfileDocument: File;
    address: string;
    telephone: string;
    emails: string[];
    website: string;
    registrarName?: string;
}

export const useCreateCouncil = () => {
    return useMutation<ApiResponse<Council>, AxiosError, CreateCouncilDTO>({
        mutationFn: async (councilData) => {
            const formData = new FormData();
            formData.append("councilName", councilData.councilName);
            formData.append("councilCode", councilData.councilCode);
            formData.append("councilProfileDocument", councilData.councilProfileDocument);
            formData.append("address", councilData.address);
            formData.append("telephone", councilData.telephone);
            formData.append("emails", JSON.stringify(councilData.emails));
            formData.append("website", councilData.website);
            if (councilData.registrarName) {
                formData.append("registrarName", councilData.registrarName);
            }
            const response = await axios.post("/tblcouncil", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            return response.data;
        },
    });
}

export interface EditCouncilDTO {
    id: string;
    councilName?: string;
    councilCode?: string;
    councilProfileDocument?: File;
    address?: string;
    telephone?: string;
    emails?: string[];
    website?: string;
    registrarName?: string;
}

export const useEditCouncil = () => {
    return useMutation<ApiResponse<Council>, AxiosError, EditCouncilDTO>({
        mutationFn: async (councilData) => {
            const formData = new FormData();
            if (councilData.councilName) {
                formData.append("councilName", councilData.councilName);
            }
            if (councilData.councilCode) {
                formData.append("councilCode", councilData.councilCode);
            }
            if (councilData.councilProfileDocument) {
                formData.append("councilProfileDocument", councilData.councilProfileDocument);
            }
            if (councilData.address) {
                formData.append("address", councilData.address);
            }
            if (councilData.telephone) {
                formData.append("telephone", councilData.telephone);
            }
            if (councilData.emails) {
                formData.append("emails", JSON.stringify(councilData.emails));
            }
            if (councilData.website) {
                formData.append("website", councilData.website);
            }
            if (councilData.registrarName !== undefined) {
                formData.append("registrarName", councilData.registrarName || "");
            }
            const response = await axios.put(`/tblcouncil/${councilData.id}`, formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });
            return response.data;
        },
    });
}

export const useDeleteCouncil = () => {
    return useMutation<ApiResponse<Council>, AxiosError, string>({
        mutationFn: async (id) => {
            const response = await axios.delete(`/tblcouncil/${id}`);
            return response.data;
        },
    });
}

export const getCouncilProfileUrl = (councilProfileDocumentPath: string): string => {
    return `${baseUrl}/files/view-application-file/${councilProfileDocumentPath}`;
}